import { format, sub } from 'date-fns';
import { useRouter } from 'next/router';
import React, { ReactNode, useEffect, useState } from 'react';
import { Chart } from 'react-google-charts';
import { useQuery } from 'react-query';
import { Error } from '@/components/ui/Error';
import { SEOMeta } from '@/components/SEOMeta';
import { postRequest } from '@/utils/apiCall';
import LoadingDots from '@/components/ui/LoadingDots';
import { renderCurrency } from '@/utils/helpers';
import { GetPnlDataResponse } from '@/utils/pnl/types';
import { useMixpanel } from '@/utils/client/useMixpanel';
import { PoweredBy } from '@/components/PoweredBy';

const PNL = (): JSX.Element => {
  const router = useRouter();
  const {
    function: { pnlVisted }
  } = useMixpanel();
  const [isVisted, setIsVisted] = useState(false);
  const [enrichedData, setEnrichedData] = useState<[string, number, number][]>(
    []
  );

  const {
    data: response,
    isError,
    isLoading,
    isSuccess
  } = useQuery(
    'get-pnl-data',
    () =>
      postRequest<GetPnlDataResponse>({
        url: '/api/get-pnl-data',
        data: {
          slug: router.query.slug
        }
      }),
    {
      enabled: !!router.query.slug,
      retry: false
    }
  );

  useEffect(() => {
    if (response?.data?.expense) {
      const perMonth: {
        [month: string]: [string, number, number];
      } = {
        [getMonth(12)]: [getMonth(12), 0, 0],
        [getMonth(11)]: [getMonth(11), 0, 0],
        [getMonth(10)]: [getMonth(10), 0, 0],
        [getMonth(9)]: [getMonth(9), 0, 0],
        [getMonth(8)]: [getMonth(8), 0, 0],
        [getMonth(7)]: [getMonth(7), 0, 0],
        [getMonth(6)]: [getMonth(6), 0, 0],
        [getMonth(5)]: [getMonth(5), 0, 0],
        [getMonth(4)]: [getMonth(4), 0, 0],
        [getMonth(3)]: [getMonth(3), 0, 0],
        [getMonth(2)]: [getMonth(12), 0, 0],
        [getMonth(1)]: [getMonth(1), 0, 0],
        [getMonth(0)]: [getMonth(0), 0, 0]
      };

      Object.entries(response.data.expense.monthly).forEach(
        ([month, expense]) => {
          if (month in perMonth) {
            perMonth[month][2] = expense;
          }
        }
      );
      Object.entries(response.data.revenue.monthly).forEach(
        ([month, revenue]) => {
          if (month in perMonth) {
            perMonth[month][1] = revenue;
          }
        }
      );
      setEnrichedData(Object.keys(perMonth).map((k) => perMonth[k]));
    } else {
      setEnrichedData([]);
    }
  }, [response]);

  useEffect(() => {
    if (response?.data?.displayName && !isVisted) {
      pnlVisted(response.data.displayName);
      setIsVisted(!isVisted);
    }
  }, [response, pnlVisted, isVisted]);

  const getMonth = (subMonth = 0): string => {
    return format(
      sub(new Date(), {
        months: subMonth
      }),
      'yyyy-MM'
    );
  };

  const dataheaders = ['Month', 'Revenue', 'Expenses'];

  const titleInfo = `${router.query.slug} ${
    isSuccess ? ` - ${response.data.displayName}` : ''
  }`;

  const renderStatement = (): ReactNode => {
    if (!response) {
      return null;
    }

    return (
      <>
        <h2 className="text-2xl text-center">Statement</h2>
        <ul role="list" className="divide-y divide-gray-200">
          <li className="relative bg-white py-5 px-4 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600">
            <div className="flex justify-between space-x-3">
              <div className="min-w-0">
                <p className="text-xl font-medium text-gray-900 truncate">
                  Income
                </p>
              </div>
              <div className="flex-shrink-0 whitespace-nowrap text-sm text-gray-500">
                {renderCurrency(response.data.revenue.total.toString(), 'USD')}
              </div>
            </div>
          </li>
          <li className="relative bg-white py-5 px-4 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600">
            <div className="flex justify-between space-x-3">
              <div className="min-w-0">
                <p className="text-xl font-medium text-gray-900 truncate">
                  Expenses
                </p>
              </div>
              <div className="flex-shrink-0 whitespace-nowrap text-sm text-gray-500">
                {renderCurrency(response.data.expense.total.toString(), 'USD')}
              </div>
            </div>
          </li>
          {Object.entries(response.data.expense.category).map(
            ([category, amount]) => (
              <li
                key={category}
                className="relative bg-white py-5 px-4 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600"
              >
                <div className="flex justify-between space-x-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate pl-10">
                      {category}
                    </p>
                  </div>
                  <div className="flex-shrink-0 whitespace-nowrap text-sm text-gray-500">
                    {renderCurrency(amount.toString(), 'USD')}
                  </div>
                </div>
              </li>
            )
          )}
          <li className="relative bg-white py-5 px-4 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600">
            <div className="flex justify-between space-x-3">
              <div className="min-w-0">
                <p className="text-2xl font-medium text-gray-900 truncate">
                  Total
                </p>
              </div>
              <div className="flex-shrink-0 whitespace-nowrap text-sm text-gray-500">
                {renderCurrency(
                  (
                    response.data.revenue.total - response.data.expense.total
                  ).toString(),
                  'USD'
                )}
              </div>
            </div>
          </li>
        </ul>
      </>
    );
  };

  const renderTotal = (): ReactNode => {
    if (!response) {
      return null;
    }

    return (
      <div className="grid grid-rows-1 sm:grid-cols-2 text-center gap-4">
        <div className="bg-white py-4">
          <div className="text-2xl">Total Revenue</div>
          <div>
            {renderCurrency(response.data.revenue.total.toString(), 'USD')}
          </div>
        </div>
        <div className="bg-white py-4">
          <div className="text-2xl">Total Expense</div>
          <div>
            {renderCurrency(response.data.expense.total.toString(), 'USD')}
          </div>
        </div>
      </div>
    );
  };

  const renderChart = (): ReactNode => {
    return (
      <Chart
        chartType="Bar"
        width="100%"
        height="600px"
        data={[dataheaders, ...enrichedData]}
      />
    );
  };

  return (
    <>
      <SEOMeta
        title={`Open P&L for ${titleInfo}`}
        description={`View profit and loss statement for ${titleInfo}`}
      />
      <div className="p-4 sm:p-8 flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          {!!isSuccess && response.data.logoUrl && (
            <img
              alt={`${response.data.displayName} logo`}
              src={
                process.env.NEXT_PUBLIC_SUPABASE_STORAGE_URL +
                response.data.logoUrl
              }
              className="w-48 h-auto"
            />
          )}
          <h1 className="text-3xl leading-6 font-medium text-gray-900">
            Open P&L {!!isSuccess && `for ${response.data.displayName}`}
          </h1>
          <div className="text-sm text-gray-600">Last 12 months</div>
        </div>
        {isLoading && <LoadingDots />}
        {isError && <Error>Failed to load</Error>}
        {isSuccess && !!response?.data?.expense && (
          <div className="flex flex-col gap-4">
            {renderTotal()}
            <div className="bg-white p-2 sm:p-8">{renderChart()}</div>

            <div className="bg-white p-1 sm:p-4">{renderStatement()}</div>

            {!response.data.hidePoweredBy && (
              <PoweredBy
                utmSource="pnl"
                prependText="Create your P&L for Free with"
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default PNL;
